/**
 * Custom hook for assignments
 */

import { useState, useEffect } from 'react';
import { useAuth } from '../../../core/contexts/AuthContext'; // Fixed import path
import assignmentService from '../services/assignmentService';
import { toast } from 'react-hot-toast';

/**
 * Custom hook to manage assignments
 * 
 * @returns {Object} Assignment methods and state
 */
const useAssignments = () => {
  const { currentUser } = useAuth();
  const [myAssignments, setMyAssignments] = useState([]);
  const [availableAssignments, setAvailableAssignments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [singleAssignment, setSingleAssignment] = useState(null);
  const [assignmentOffers, setAssignmentOffers] = useState([]);
  const [offerLoading, setOfferLoading] = useState(false);

  // Load user's assignments
  const loadMyAssignments = async () => {
    if (!currentUser) return;
    
    try {
      setLoading(true);
      const assignments = await assignmentService.getUserAssignments(currentUser.uid);
      setMyAssignments(assignments);
    } catch (error) {
      console.error('Error loading my assignments:', error);
      toast.error('Failed to load your assignments');
    } finally {
      setLoading(false);
    }
  };

  // Load available assignments
  const loadAvailableAssignments = async (filters = {}) => {
    if (!currentUser) return;
    
    try {
      setLoading(true);
      const assignments = await assignmentService.getAvailableAssignments(filters);
      setAvailableAssignments(assignments);
    } catch (error) {
      console.error('Error loading available assignments:', error);
      toast.error('Failed to load available assignments');
    } finally {
      setLoading(false);
    }
  };

  // Create a new assignment
  const createAssignment = async (data, file) => {
    if (!currentUser) return;
    
    try {
      setUploading(true);
      const assignmentId = await assignmentService.createAssignment(data, file);
      toast.success('Assignment created successfully');
      await loadMyAssignments();
      return assignmentId;
    } catch (error) {
      console.error('Error creating assignment:', error);
      toast.error('Failed to create assignment');
      return null;
    } finally {
      setUploading(false);
    }
  };

  // Delete an assignment
  const deleteAssignment = async (assignmentId) => {
    if (!currentUser) return;
    
    try {
      setLoading(true);
      await assignmentService.deleteAssignment(assignmentId);
      toast.success('Assignment deleted successfully');
      await loadMyAssignments();
    } catch (error) {
      console.error('Error deleting assignment:', error);
      toast.error('Failed to delete assignment');
    } finally {
      setLoading(false);
    }
  };

  // Load assignment by ID
  const loadAssignmentById = async (assignmentId) => {
    if (!currentUser) return;
    
    try {
      setLoading(true);
      const assignment = await assignmentService.getAssignmentById(assignmentId);
      setSingleAssignment(assignment);
      return assignment;
    } catch (error) {
      console.error('Error loading assignment:', error);
      toast.error('Failed to load assignment details');
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Load offers for an assignment
  const loadAssignmentOffers = async (assignmentId) => {
    if (!currentUser) return;
    
    try {
      setOfferLoading(true);
      const offers = await assignmentService.getAssignmentOffers(assignmentId);
      setAssignmentOffers(offers);
      return offers;
    } catch (error) {
      console.error('Error loading assignment offers:', error);
      toast.error('Failed to load offers');
      return [];
    } finally {
      setOfferLoading(false);
    }
  };

  // Create an offer for an assignment
  const createOffer = async (assignmentId, offerData) => {
    if (!currentUser) return;
    
    try {
      setOfferLoading(true);
      const offerId = await assignmentService.createOffer(assignmentId, offerData);
      toast.success('Your offer has been submitted');
      return offerId;
    } catch (error) {
      console.error('Error creating offer:', error);
      toast.error('Failed to submit offer');
      return null;
    } finally {
      setOfferLoading(false);
    }
  };

  // Accept an offer
  const acceptOffer = async (assignmentId, offerId) => {
    if (!currentUser) return;
    
    try {
      setOfferLoading(true);
      await assignmentService.acceptOffer(assignmentId, offerId);
      toast.success('Offer accepted successfully');
      // Reload assignment to get updated status
      await loadAssignmentById(assignmentId);
      await loadAssignmentOffers(assignmentId);
      await loadMyAssignments();
      return true;
    } catch (error) {
      console.error('Error accepting offer:', error);
      toast.error('Failed to accept offer');
      return false;
    } finally {
      setOfferLoading(false);
    }
  };

  // Mark assignment as completed
  const completeAssignment = async (assignmentId) => {
    if (!currentUser) return;
    
    try {
      setLoading(true);
      await assignmentService.completeAssignment(assignmentId);
      toast.success('Assignment marked as completed');
      // Reload assignment to get updated status
      await loadAssignmentById(assignmentId);
      await loadMyAssignments();
      return true;
    } catch (error) {
      console.error('Error completing assignment:', error);
      toast.error('Failed to complete assignment');
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    myAssignments,
    availableAssignments,
    singleAssignment,
    assignmentOffers,
    loading,
    uploading,
    offerLoading,
    loadMyAssignments,
    loadAvailableAssignments,
    createAssignment,
    deleteAssignment,
    loadAssignmentById,
    loadAssignmentOffers,
    createOffer,
    acceptOffer,
    completeAssignment
  };
};

export default useAssignments;